import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import {ChangeEvent, useContext, useEffect, useMemo, useRef, useState} from 'react'
import styled from 'styled-components'
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core'
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {v4 as uuidv4} from 'uuid'
import MinutaStep, {
    MinutaData,
    createDefaultCriterios,
    RuleStructure,
} from './MinutaStep'
import TemplateConfigsSection from './TemplateConfigsSection'
import {useNavigate, useLocation} from 'react-router-dom'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import {HistoryContext} from '../../contexts/history'
import {AdminCoreApi} from '../../services/AdminCoreApi.service'
import {FlowApi} from '../../services/FlowApi.service'
import {FlowDocumentType, ProjectType, QuestionFlow} from '../../types/flowData'
import {Organization} from '../../types/orgUsers'

type DocumentTemplateDraft = {
    path: string | null
    content_path: string | null
    content: string | null
}

type DocumentTemplateData = {
    minuta: string | null
    prompt: string
    template: {
        template_path: string | null
    }
    draft: DocumentTemplateDraft
}

type DocumentParametersItem = {
    question_uuid: string | null
    conditions: ConditionEntry[]
    templates: Record<string, DocumentTemplateData>
}

type DocumentParametersMap = Record<string, DocumentParametersItem>

type DocumentParameterEntry = {
    documentTypeId: string
    value: DocumentParametersItem
}

type ApiCriterioItem = {
    question_uuid: string
    values: string[]
}

type ConditionRule = {
    AND: ApiCriterioItem[]
}

type ConditionEntry = {
    result: string
    rule: ConditionRule
}

const Section = styled.section`
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;
    background-color: #f6f8fa;
    padding: 0;
    overflow-y: auto;
`

const DropdownWrap = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 16px 24px;
`

const DropdownField = styled.label`
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: #1f2328;
    font-size: 14px;
    font-weight: 600;
    margin-top: 16px;
    max-width: 280px;
`

const FormBox = styled.div`
    width: 100%;
    max-width: 898px;
    min-height: 241px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border: 1px solid #d0d7de;
    border-radius: 8px;
    padding: 24px;
    background-color: #ffffff;
    margin: 24px auto 0;
    box-sizing: border-box;
`

const FormTitle = styled.h3`
    font-family: Roboto, sans-serif;
    font-weight: 600;
    font-size: 18px;
    line-height: 27px;
    letter-spacing: 0px;
    color: #1f2328;
    margin: 0;
`

const SectionTitle = styled.h2`
    font-family: Roboto, sans-serif;
    font-weight: 600;
    font-size: 24px;
    line-height: 36px;
    letter-spacing: 0px;
    color: #1f2328;
    margin: 24px 0 0;
    width: 100%;
    max-width: 898px;
    box-sizing: border-box;
`

const UploadField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const UploadLabel = styled.label`
    font-family: Roboto, sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0px;
    color: #1f2328;
`

const UploadInput = styled.input`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d0d7de;
    border-radius: 6px;
    background-color: #ffffff;
    font-size: 14px;
    color: #24292f;
`

const SelectedFile = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 15px 12px;
    border-radius: 6px;
    background-color: #f6f8fa;
    color: #24292f;
    font-size: 14px;
`

const SelectedFileInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const RemoveFileButton = styled.button`
    border: none;
    background: transparent;
    color: #d1242f;
    font-size: 14px;
    cursor: pointer;
    padding: 0;
`

const Footer = styled.footer`
    width: 100%;
    height: 100px;
    background-color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    margin-top: auto;
`

const MinutasWrapper = styled.div`
    width: 100%;
    max-width: 898px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 16px auto 24px;
    box-sizing: border-box;
`

const PromptCard = styled.div`
    border: 1px solid #d0d7de;
    border-radius: 8px;
    background-color: #ffffff;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const QuestionTitle = styled.label`
    font-family: Roboto, sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0px;
    color: #1f2328;
`

const QuestionSubtitle = styled.p`
    font-family: Roboto, sans-serif;
    font-weight: 400;
    font-size: 13px;
    line-height: 19.5px;
    letter-spacing: 0px;
    color: #838383;
    margin: 0;
`

const PromptTextArea = styled.textarea`
    width: 100%;
    min-height: 120px;
    padding: 12px;
    border: 1px solid #d0d7de;
    border-radius: 8px;
    font-size: 14px;
    color: #24292f;
    resize: vertical;
`

const normalizeConditions = (conditions: unknown): ConditionEntry[] => {
    if (!Array.isArray(conditions)) {
        return []
    }

    return conditions
        .map((entry) => {
            if (!entry || typeof entry !== 'object') {
                return null
            }

            const raw = entry as Partial<ConditionEntry>
            if (!raw.result || typeof raw.result !== 'string') {
                return null
            }

            const ruleAnd: ApiCriterioItem[] = Array.isArray(raw.rule?.AND)
                ? (raw.rule!.AND as any[]).map((item) => ({
                      question_uuid: typeof item?.question_uuid === 'string'
                          ? item.question_uuid
                          : (item?.question_uuid?.$uuid ?? ''),
                      values: Array.isArray(item?.values) ? item.values : [],
                  }))
                : []

            return {
                result: raw.result,
                rule: {AND: ruleAnd},
            }
        })
        .filter((entry): entry is ConditionEntry => Boolean(entry))
}

const normalizeDocumentParameterValue = (
    value: unknown
): DocumentParametersItem => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return {
            question_uuid: null,
            conditions: [],
            templates: {},
        }
    }

    const raw = value as Partial<DocumentParametersItem>
    const templatesRaw =
        raw.templates && typeof raw.templates === 'object' && !Array.isArray(raw.templates)
            ? raw.templates
            : {}

    const templates: Record<string, DocumentTemplateData> = {}

    Object.entries(templatesRaw).forEach(([key, templateValue]) => {
        if (!templateValue || typeof templateValue !== 'object') {
            templates[key] = {
                minuta: null,
                prompt: '',
                template: {template_path: null},
                draft: {path: null, content_path: null, content: null},
            }
            return
        }

        const template = templateValue as Partial<DocumentTemplateData>
        const draft = template.draft as Partial<DocumentTemplateDraft> | undefined

        templates[key] = {
            minuta: typeof template.minuta === 'string' ? template.minuta : null,
            prompt: typeof template.prompt === 'string' ? template.prompt : '',
            template: {
                template_path:
                    typeof template.template?.template_path === 'string'
                        ? template.template.template_path
                        : null,
            },
            draft: {
                path: typeof draft?.path === 'string' ? draft.path : null,
                content_path:
                    typeof draft?.content_path === 'string'
                        ? draft.content_path
                        : null,
                content: typeof draft?.content === 'string' ? draft.content : null,
            },
        }
    })

    return {
        question_uuid:
            typeof raw.question_uuid === 'string' ? raw.question_uuid : null,
        conditions: normalizeConditions(raw.conditions),
        templates,
    }
}

const normalizeDocumentParameters = (
    documentParameters: unknown
): DocumentParameterEntry[] => {
    if (!documentParameters) {
        return []
    }

    if (Array.isArray(documentParameters)) {
        return documentParameters.flatMap((entry) => {
            if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
                return []
            }

            return Object.entries(entry).map(([documentTypeId, value]) => ({
                documentTypeId,
                value: normalizeDocumentParameterValue(value),
            }))
        })
    }

    if (typeof documentParameters !== 'object') {
        return []
    }

    return Object.entries(documentParameters).map(([documentTypeId, value]) => ({
        documentTypeId,
        value: normalizeDocumentParameterValue(value),
    }))
}

const getFileNameFromPath = (path?: string | null): string | null => {
    if (!path) {
        return null
    }

    const parts = path.split('/')
    return parts[parts.length - 1] || null
}

const extractDocumentParameters = (flow: unknown): unknown => {
    if (!flow || typeof flow !== 'object') {
        return null
    }

    const flowData = flow as any

    if (flowData.document_parameters) {
        return flowData.document_parameters
    }

    if (Array.isArray(flowData.questions)) {
        for (const questionNode of flowData.questions) {
            const fromNode = questionNode?.document_parameters
            const fromQuestionData = questionNode?.question_data?.document_parameters

            if (fromNode) {
                return fromNode
            }

            if (fromQuestionData) {
                return fromQuestionData
            }
        }
    }

    return null
}

const createMinuta = (
    documentTypeId = '',
    criterios: RuleStructure = createDefaultCriterios()
): MinutaData => {
    const baseTime = Date.now()

    return {
        id: uuidv4(),
        templateName: '',
        tipoDocumento: documentTypeId,
        minutaFileName: null,
        minutaFile: null,
        minutaContentFileName: null,
        minutaContentFile: null,
        prompt: '',
        criterios,
        timbradoFileName: null,
        timbradoFile: null,
        forceExtraction: false,
        minutaFileKey: baseTime,
        minutaContentFileKey: baseTime + 1,
        timbradoFileKey: baseTime + 2,
    }
}

const getConditionsForTemplate = (
    conditions: ConditionEntry[],
    templateKey: string
): RuleStructure['AND'] => {
    const match = conditions.find((entry) => entry.result === templateKey)
    if (match?.rule?.AND?.length) {
        return match.rule.AND.map((item) => ({
            id: uuidv4(),
            question_uuid: item.question_uuid,
            values: item.values,
        }))
    }

    return createDefaultCriterios().AND
}

const createMinutasFromDocumentParameters = (
    entries: DocumentParameterEntry[]
): MinutaData[] =>
    entries.flatMap((entry) => {
        const conditions = normalizeConditions(entry.value.conditions)
        const templates = Object.entries(entry.value.templates)

        if (templates.length === 0) {
            return [
                createMinuta(entry.documentTypeId, {
                    AND: createDefaultCriterios().AND,
                }),
            ]
        }

        return templates.map(([templateKey, templateValue]) => {
            const draftPath = templateValue.draft?.path ?? templateValue.minuta
            const contentPath = templateValue.draft?.content_path ?? null
            const timbradoPath = templateValue.template?.template_path ?? null

            return {
                ...createMinuta(entry.documentTypeId, {
                    AND: getConditionsForTemplate(conditions, templateKey),
                }),
                templateName: templateKey,
                prompt: templateValue.prompt ?? '',
                minutaFileName: getFileNameFromPath(draftPath),
                minutaContentFileName: getFileNameFromPath(contentPath),
                timbradoFileName: getFileNameFromPath(timbradoPath),
            }
        })
    })

const buildDocumentParametersPayload = (
    minutas: MinutaData[],
    existingParameters: DocumentParametersMap,
    promptFallback: string
): DocumentParametersMap => {
    const payload: DocumentParametersMap = {}

    minutas.forEach((minuta) => {
        if (!minuta.tipoDocumento) {
            return
        }

        const documentTypeId = minuta.tipoDocumento
        const existingEntry = existingParameters[documentTypeId]
        const templateKey = minuta.templateName || 'default'

        if (!payload[documentTypeId]) {
            payload[documentTypeId] = {
                question_uuid: existingEntry?.question_uuid ?? null,
                conditions: [],
                templates: {},
            }
        }

        const existingTemplate =
            existingEntry?.templates?.[templateKey] ??
            (templateKey === 'default'
                ? existingEntry?.templates?.default
                : undefined)
        const draftPath =
            existingTemplate?.draft?.path ?? minuta.minutaFileName ?? null
        const templatePath =
            existingTemplate?.template?.template_path ??
            minuta.minutaContentFileName ??
            null
        const prompt =
            minuta.prompt || existingTemplate?.prompt || promptFallback || ''

        payload[documentTypeId].templates[templateKey] = {
            minuta: existingTemplate?.minuta ?? draftPath,
            prompt,
            template: {template_path: templatePath},
            draft: {
                path: draftPath ?? '',
                content_path: existingTemplate?.draft?.content_path ?? null,
                content: existingTemplate?.draft?.content ?? null,
            },
        }

        if (minuta.criterios?.AND?.length) {
            const andItems: ApiCriterioItem[] = minuta.criterios.AND
                .filter((item) => {
                    const ci = item as {question_uuid?: string}
                    return !!ci.question_uuid
                })
                .map((item) => {
                    const ci = item as {id: string; question_uuid: string; values: string[]}
                    return {question_uuid: ci.question_uuid, values: ci.values}
                })
            if (andItems.length) {
                payload[documentTypeId].conditions.push({
                    result: templateKey,
                    rule: {AND: andItems},
                })
            }
        }
    })

    Object.entries(payload).forEach(([documentTypeId, entry]) => {
        if (entry.conditions.length === 0) {
            const existing = existingParameters[documentTypeId]
            entry.conditions = normalizeConditions(existing?.conditions)
        }
    })

    return payload
}

export default function uploadDocumentsPage() {
    const {flows, setFlowData, flowQuestions, userDocumentsInfo, getFlow} = useContext(HistoryContext)

    const navigate = useNavigate()
    const location = useLocation()
    const routeState = location.state as {
        flow?: QuestionFlow
        document_flow_id?: string
        organization_uuid?: string
    } | null
    const flowState = routeState?.flow
    const navFlowId = routeState?.document_flow_id ?? null
    const navOrganizationUuid = routeState?.organization_uuid ?? null
    const [selectedFlowId, setSelectedFlowId] = useState<string>(
        flowState?.id ?? navFlowId ?? ''
    )
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('')
    const [flowOrganizationIds, setFlowOrganizationIds] = useState<Set<string> | null>(null)
    const [loadingFlow, setLoadingFlow] = useState(false)
    const [isProcessing, setIsProcessing] = useState(false)
    const latestFlowChangeRef = useRef<string | null>(null)

    const selectedFlow = flowQuestions ?? flowState
    const documentParameterEntries = useMemo(
        () =>
            normalizeDocumentParameters(
                extractDocumentParameters(selectedFlow)
            ),
        [selectedFlow]
    )
    const existingDocumentParameters = useMemo(() => {
        const map: DocumentParametersMap = {}
        documentParameterEntries.forEach((entry) => {
            map[entry.documentTypeId] = entry.value
        })
        return map
    }, [documentParameterEntries])

    const handleFlowChange = async (id: string) => {
        latestFlowChangeRef.current = id
        setSelectedFlowId(id)
        setLoadingFlow(true)
        try {
            await setFlowData(id)
            try {
                const linkedOrgs = await flowApi.getFlowOrganizations(id)
                if (latestFlowChangeRef.current !== id) return
                if (Array.isArray(linkedOrgs) && linkedOrgs.length > 0) {
                    const ids = new Set<string>(linkedOrgs.map((org: {id: string}) => org.id))
                    setFlowOrganizationIds(ids)
                    setSelectedOrganizationId((current) => (ids.has(current) ? current : ''))
                } else {
                    // flow "Default" (compartilhado) ou sem organizações vinculadas: não restringe
                    setFlowOrganizationIds(null)
                }
            } catch (error) {
                console.error('Erro ao obter organizações do fluxo:', error)
            }
        } finally {
            if (latestFlowChangeRef.current === id) {
                setLoadingFlow(false)
            }
        }
    }
    const [timbradoFileName, setTimbradoFileName] = useState<string | null>(
        null
    )
    const [timbradoFile, setTimbradoFile] = useState<File | null>(null)
    const timbradoInputRef = useRef<HTMLInputElement | null>(null)

    const handleTimbradoChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null
        setTimbradoFileName(file ? file.name : null)
        setTimbradoFile(file)
    }

    const handleTimbradoRemove = () => {
        setTimbradoFileName(null)
        setTimbradoFile(null)
        if (timbradoInputRef.current) {
            timbradoInputRef.current.value = ''
        }
    }

    const [minutas, setMinutas] = useState<MinutaData[]>([])
    const [allMinutasCollapsed, setAllMinutasCollapsed] = useState(false)
    const [promptGeracao, setPromptGeracao] = useState('')
    const [organizations, setOrganizations] = useState<Organization[]>([])
    const [projectTypes, setProjectTypes] = useState<ProjectType[]>([])
    const [selectedProjectTypeId, setSelectedProjectTypeId] = useState<string>('')
    const [documentTypes, setDocumentTypes] = useState<FlowDocumentType[]>([])
    const [documentParametersPayload, setDocumentParametersPayload] =
        useState<DocumentParametersMap>({})

    useEffect(() => {
        getFlow()
        if (navFlowId && !flowState) {
            handleFlowChange(navFlowId)
        }
    }, [])

    useEffect(() => {
        let isMounted = true

        const loadOrganizations = async () => {
            try {
                const adminCoreApi = new AdminCoreApi()
                const data = await adminCoreApi.getOrganizations()
                if (isMounted) {
                    setOrganizations(Array.isArray(data) ? data : [])
                    if (navOrganizationUuid) {
                        setSelectedOrganizationId(navOrganizationUuid)
                    }
                }
            } catch (error) {
                console.error('Erro ao carregar organizações:', error)
            }
        }

        loadOrganizations()

        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        let isMounted = true

        const loadProjectTypes = async () => {
            try {
                const data = await flowApi.getProjectTypes()
                if (isMounted && Array.isArray(data)) {
                    setProjectTypes(data)
                    // pré-seleciona o primeiro para os tipos de documento não abrirem vazios
                    setSelectedProjectTypeId((current) => current || data[0]?.id || '')
                }
            } catch (error) {
                console.error('Erro ao carregar project types:', error)
            }
        }

        loadProjectTypes()

        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        if (!selectedProjectTypeId) {
            return
        }
        let isMounted = true

        const loadDocumentTypes = async () => {
            try {
                const data = await flowApi.getProjectTypeDocumentTypes(selectedProjectTypeId)
                if (isMounted) {
                    setDocumentTypes(Array.isArray(data) ? data : [])
                }
            } catch (error) {
                console.error('Erro ao carregar tipos de documento:', error)
            }
        }

        loadDocumentTypes()

        return () => {
            isMounted = false
        }
    }, [selectedProjectTypeId])

    const baselineRef = useRef<DocumentParametersMap>({})
    const visibleOrganizations = useMemo(
        () =>
            flowOrganizationIds
                ? organizations.filter((org) => flowOrganizationIds.has(org.guid))
                : organizations,
        [organizations, flowOrganizationIds]
    )

    useEffect(() => {
        if (documentParameterEntries.length === 0) {
            baselineRef.current = {}
            setMinutas([])
            return
        }

        const initial = createMinutasFromDocumentParameters(documentParameterEntries)
        // baseline gerado pelo mesmo builder do payload: comparar com o dado cru do servidor sempre difere no formato
        baselineRef.current = buildDocumentParametersPayload(
            initial,
            existingDocumentParameters,
            promptGeracao
        )
        setMinutas(initial)
    }, [documentParameterEntries])

    useEffect(() => {
        const flow = selectedFlow as any
        const templatePath = flow?.template_default?.template_path ?? null
        setTimbradoFileName(templatePath ? getFileNameFromPath(templatePath) : null)
        setTimbradoFile(null)
        if (!flow) return
        // flow legado (sem project_type_id) volta ao primeiro tipo, em vez de herdar o do flow anterior
        setSelectedProjectTypeId(flow.project_type_id ?? projectTypes[0]?.id ?? '')
    }, [selectedFlow])

    useEffect(() => {
        setDocumentParametersPayload(
            buildDocumentParametersPayload(
                minutas,
                existingDocumentParameters,
                promptGeracao
            )
        )
    }, [minutas, existingDocumentParameters, promptGeracao])

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 4}})
    )

    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event
        if (!over || active.id === over.id) {
            return
        }

        setMinutas((items) => {
            const oldIndex = items.findIndex((item) => item.id === active.id)
            const newIndex = items.findIndex((item) => item.id === over.id)
            return arrayMove(items, oldIndex, newIndex)
        })
    }

    const handleMinutaUpdate = (id: string, patch: Partial<MinutaData>) => {
        setMinutas((items) =>
            items.map((item) => (item.id === id ? {...item, ...patch} : item))
        )
    }

    const allForceExtraction =
        minutas.length > 0 && minutas.every((item) => item.forceExtraction)

    const handleForceExtractionAll = (checked: boolean) => {
        setMinutas((items) =>
            items.map((item) => ({...item, forceExtraction: checked}))
        )
    }

    const handleAddMinuta = () => {
        setMinutas((items) => [...items, createMinuta()])
    }

    const handleRemoveMinuta = (id: string) => {
        setMinutas((items) => items.filter((item) => item.id !== id))
    }

    const flowApi = new FlowApi()

    type MinutaUploadUrls = {
        minutaUrl: string | null
        contentUrl: string | null
        timbradoUrl: string | null
    }

    const uploadMinutaFiles = async (): Promise<{
        generalTimbradoUrl: string | null
        byMinutaId: Record<string, MinutaUploadUrls>
    }> => {
        let generalTimbradoUrl: string | null = null

        if (timbradoFile) {
            generalTimbradoUrl = await flowApi.getUploadPreSignedUrl(
                {
                    filename: 'template.docx',
                    organization_id: selectedOrganizationId,
                },
                timbradoFile
            )
        }

        const byMinutaId: Record<string, MinutaUploadUrls> = {}

        for (const minuta of minutas) {
            const documentTypeId = minuta.tipoDocumento || null
            const urls: MinutaUploadUrls = {
                minutaUrl: null,
                contentUrl: null,
                timbradoUrl: null,
            }

            if (minuta.minutaFile) {
                urls.minutaUrl = await flowApi.getUploadPreSignedUrl(
                    {
                        filename: 'draft.docx',
                        organization_id: selectedOrganizationId,
                        document_type_id: documentTypeId,
                        document_key: minuta.templateName || null,
                    },
                    minuta.minutaFile
                )
            }

            if (minuta.minutaContentFile) {
                urls.contentUrl = await flowApi.getUploadPreSignedUrl(
                    {
                        filename: 'draft.txt',
                        organization_id: selectedOrganizationId,
                        document_type_id: documentTypeId,
                        document_key: minuta.templateName || null,
                    },
                    minuta.minutaContentFile
                )
            }

            if (minuta.timbradoFile) {
                urls.timbradoUrl = await flowApi.getUploadPreSignedUrl(
                    {
                        filename: 'template.docx',
                        organization_id: selectedOrganizationId,
                        document_type_id: documentTypeId,
                        document_key: minuta.templateName || null,
                    },
                    minuta.timbradoFile
                )
            }

            byMinutaId[minuta.id] = urls
        }

        return {generalTimbradoUrl, byMinutaId}
    }

    const mergeUploadUrlsIntoPayload = (
        payload: DocumentParametersMap,
        uploadResult: Awaited<ReturnType<typeof uploadMinutaFiles>>
    ): DocumentParametersMap => {
        const {generalTimbradoUrl, byMinutaId} = uploadResult
        const result: DocumentParametersMap = JSON.parse(JSON.stringify(payload))

        for (const minuta of minutas) {
            const typeId = minuta.tipoDocumento
            const templateKey = minuta.templateName || 'default'
            if (!typeId || !result[typeId]?.templates?.[templateKey]) continue

            const urls = byMinutaId[minuta.id]
            const tmpl = result[typeId].templates[templateKey]

            if (urls.minutaUrl) {
                tmpl.draft.path = urls.minutaUrl
                tmpl.minuta = urls.minutaUrl
            }
            if (urls.contentUrl) {
                tmpl.draft.content_path = urls.contentUrl
            }
            if (urls.timbradoUrl) {
                tmpl.template.template_path = urls.timbradoUrl
            }
        }

        return result
    }

    const handleProcessar = async () => {
        if (!selectedFlowId || !selectedOrganizationId) {
            console.error('Flow ou organização selecionada não encontrada.')
            return
        }

        setIsProcessing(true)
        try {
            const uploadResult = await uploadMinutaFiles()
            const finalPayload = mergeUploadUrlsIntoPayload(
                documentParametersPayload,
                uploadResult
            )

            // PATCH: só documentos alterados (ou com upload novo) vão no payload
            const changedPayload: DocumentParametersMap = {}
            Object.keys(finalPayload).forEach((typeId) => {
                if (
                    JSON.stringify(finalPayload[typeId]) !==
                    JSON.stringify(baselineRef.current[typeId])
                ) {
                    changedPayload[typeId] = finalPayload[typeId]
                }
            })

            const forceExtractionList = minutas
                .filter((m) => m.forceExtraction && m.tipoDocumento)
                .map((m) => ({
                    document_type_id: m.tipoDocumento,
                    template_key: m.templateName || 'default',
                }))

            // extração forçada precisa do documento no payload (o PATCH extrai só os enviados)
            forceExtractionList.forEach(({document_type_id}) => {
                if (finalPayload[document_type_id]) {
                    changedPayload[document_type_id] = finalPayload[document_type_id]
                }
            })

            // PATCH só adiciona/atualiza; remoção exige PUT com payload completo
            const hasRemoval = Object.keys(existingDocumentParameters).some(
                (id) => !(id in finalPayload)
            )

            const projectTypeChanged =
                !!selectedProjectTypeId &&
                selectedProjectTypeId !== selectedFlow?.project_type_id

            if (
                !hasRemoval &&
                Object.keys(changedPayload).length === 0 &&
                !uploadResult.generalTimbradoUrl &&
                forceExtractionList.length === 0 &&
                !projectTypeChanged
            ) {
                return
            }

            const extras = {
                organization_uuid: selectedOrganizationId,
                ...(selectedProjectTypeId && {
                    project_type_id: selectedProjectTypeId,
                }),
                ...(uploadResult.generalTimbradoUrl && {
                    template_default: {
                        template_path: uploadResult.generalTimbradoUrl,
                    },
                }),
                ...(forceExtractionList.length > 0 && {
                    force_extraction: forceExtractionList,
                }),
            }

            // o PATCH exige >= 1 documento: timbrado geral ou project type sozinhos caem no PUT
            if (hasRemoval || Object.keys(changedPayload).length === 0) {
                await flowApi.updateDocumentParameters(selectedFlowId, {
                    body: {...extras, document_parameters: finalPayload},
                })
            } else {
                await flowApi.patchDocumentParameters(selectedFlowId, {
                    body: {...extras, document_parameters: changedPayload},
                })
            }

            await setFlowData(selectedFlowId)
        } catch (error) {
            console.error('Erro ao processar documentos:', error)
            navigate('/error')
        } finally {
            setIsProcessing(false)
        }
    }

    const handleMinutaFileChange = (id: string, file: File | null) => {
        handleMinutaUpdate(id, {
            minutaFileName: file ? file.name : null,
            minutaFile: file,
        })
    }

    const handleMinutaFileRemove = (id: string) => {
        handleMinutaUpdate(id, {
            minutaFileName: null,
            minutaFile: null,
            minutaFileKey: Date.now(),
        })
    }

    const handleTemplateFileChange = (id: string, file: File | null) => {
        handleMinutaUpdate(id, {
            minutaContentFileName: file ? file.name : null,
            minutaContentFile: file,
        })
    }

    const handleTemplateFileRemove = (id: string) => {
        handleMinutaUpdate(id, {
            minutaContentFileName: null,
            minutaContentFile: null,
            minutaContentFileKey: Date.now(),
        })
    }

    const handleTimbradoFileChange = (id: string, file: File | null) => {
        handleMinutaUpdate(id, {
            timbradoFileName: file ? file.name : null,
            timbradoFile: file,
        })
    }

    const handleTimbradoFileRemove = (id: string) => {
        handleMinutaUpdate(id, {
            timbradoFileName: null,
            timbradoFile: null,
            timbradoFileKey: Date.now(),
        })
    }


    return (
        <Section>
            <DropdownWrap>
                <div style={{ alignSelf: 'flex-end' }}>
                    <Button onClick={() => navigate('/adminPage')}>
                        <KeyboardBackspaceIcon /> Voltar para o Menu Admin
                    </Button>
                </div>
                <DropdownField>
                    <Autocomplete
                        options={flows}
                        getOptionLabel={(flow) => flow.name}
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        value={
                            flows.find((flow) => flow.id === selectedFlowId) ||
                            null
                        }
                        onChange={(_event, newValue) => {
                            if (newValue) handleFlowChange(newValue.id)
                        }}
                        disabled={loadingFlow}
                        size="small"
                        sx={{minWidth: '260px'}}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={
                                    loadingFlow
                                        ? 'Carregando...'
                                        : 'Selecionar o fluxo'
                                }
                            />
                        )}
                    />
                </DropdownField>
                <DropdownField>
                    <Autocomplete
                        options={projectTypes}
                        getOptionLabel={(projectType) => projectType.name}
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        value={
                            projectTypes.find(
                                (projectType) => projectType.id === selectedProjectTypeId
                            ) || null
                        }
                        onChange={(_event, newValue) => {
                            if (newValue) setSelectedProjectTypeId(newValue.id)
                        }}
                        size="small"
                        sx={{minWidth: '260px'}}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Selecionar o tipo de projeto"
                            />
                        )}
                    />
                </DropdownField>
                <DropdownField>
                    <Autocomplete
                        options={visibleOrganizations}
                        getOptionLabel={(org) => org.name}
                        getOptionKey={(org) => org.guid}
                        isOptionEqualToValue={(option, value) =>
                            option.guid === value.guid
                        }
                        value={
                            visibleOrganizations.find(
                                (org) => org.guid === selectedOrganizationId
                            ) || null
                        }
                        onChange={(_event, newValue) => {
                            if (newValue)
                                setSelectedOrganizationId(newValue.guid)
                        }}
                        disabled={loadingFlow}
                        size="small"
                        sx={{minWidth: '260px'}}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={
                                    loadingFlow
                                        ? 'Carregando...'
                                        : 'Selecionar a organização'
                                }
                            />
                        )}
                    />
                </DropdownField>
            </DropdownWrap>
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '898px',
                    minHeight: '151.890625px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    opacity: 1,
                    borderRadius: '8px',
                    border: '1px solid #d0d7de',
                    padding: '25px',
                    backgroundColor: '#ffffff',
                    margin: '0 auto',
                    boxSizing: 'border-box',
                }}
            >
                <Typography variant="h5" sx={{fontWeight: 700, color: '#1f2b3a'}}>
                    Upload de documentos do cliente
                </Typography>
                <Typography variant="body2" sx={{color: '#5b6b7b', mb: 3}}>
                    Configure os documentos e minutas da organização
                </Typography>
            </Box>
            <FormBox>
                <FormTitle>Timbrado</FormTitle>
                <UploadField>
                    <UploadLabel htmlFor="timbrado-upload">
                        Modelo timbrado da organização *
                    </UploadLabel>
                    {timbradoFileName ? (
                        <SelectedFile>
                            <SelectedFileInfo>
                                <DescriptionOutlinedIcon fontSize="small" />
                                {timbradoFileName}
                            </SelectedFileInfo>
                            <RemoveFileButton
                                type="button"
                                onClick={handleTimbradoRemove}
                                aria-label="Remover arquivo"
                            >
                                x
                            </RemoveFileButton>
                        </SelectedFile>
                    ) : (
                        <UploadInput
                            id="timbrado-upload"
                            ref={timbradoInputRef}
                            type="file"
                            accept=".docx"
                            onChange={handleTimbradoChange}
                        />
                    )}
                    <Alert severity="info" style={{marginTop: '10px'}}>
                        O modelo deve conter apenas cabeçalho e rodapé. Não
                        inclua conteúdo no corpo do documento.
                    </Alert>
                </UploadField>
            </FormBox>

            <TemplateConfigsSection
                flowId={selectedFlowId}
                organizationId={selectedOrganizationId}
            />

            <SectionTitle>Configuração de Minutas</SectionTitle>
            <MinutasWrapper>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <Box sx={{display: 'flex', marginBottom: '12px'}}>
                        <Button
                            variant="outlined"
                            onClick={handleAddMinuta}
                            fullWidth
                            sx={{
                                borderStyle: 'dashed',
                                borderColor: '#3ACDB4',
                                color: '#3ACDB4',
                            }}
                        >
                            Adicionar minuta
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '12px',
                        }}
                    >
                        <input
                            type="checkbox"
                            id="force-extraction-all"
                            checked={allForceExtraction}
                            onChange={(e) =>
                                handleForceExtractionAll(e.target.checked)
                            }
                        />
                        <QuestionTitle htmlFor="force-extraction-all">
                            Forçar extração do manifesto em todas
                        </QuestionTitle>
                    </Box>
                    <Box sx={{display: 'flex', marginBottom: '12px'}}>
                        <Button
                            size="small"
                            variant="text"
                            startIcon={
                                allMinutasCollapsed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />
                            }
                            onClick={() => setAllMinutasCollapsed((prev) => !prev)}
                            sx={{color: '#57606a', textTransform: 'none'}}
                        >
                            {allMinutasCollapsed ? 'Maximizar' : 'Minimizar'}
                        </Button>
                    </Box>
                    <SortableContext
                        items={minutas.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <Stepper
                            orientation="vertical"
                            sx={{
                                '& .MuiStepIcon-root.Mui-completed': {
                                    color: '#4CAF50',
                                },
                            }}
                        >
                            {minutas.map((item, index) => (
                                <MinutaStep
                                    key={item.id}
                                    item={item}
                                    index={index}
                                    onUpdate={handleMinutaUpdate}
                                    onRemove={handleRemoveMinuta}
                                    onMinutaFileChange={handleMinutaFileChange}
                                    onMinutaFileRemove={handleMinutaFileRemove}
                                    onTemplateFileChange={handleTemplateFileChange}
                                    onTemplateFileRemove={handleTemplateFileRemove}
                                    onTimbradoFileChange={
                                        handleTimbradoFileChange
                                    }
                                    onTimbradoFileRemove={
                                        handleTimbradoFileRemove
                                    }
                                    flowQuestions={flowQuestions?.questions ?? flowState?.questions}
                                    fixedMinutas={documentParameterEntries.length > 0}
                                    collapseAll={allMinutasCollapsed}
                                    documentTypes={documentTypes}
                                />
                            ))}
                            <Step expanded>
                                <StepLabel>Prompt de geração</StepLabel>
                                <StepContent>
                                    <PromptCard>
                                        <QuestionTitle>
                                            Prompt de geração
                                        </QuestionTitle>
                                        <QuestionSubtitle>
                                            Prompt da IA
                                        </QuestionSubtitle>
                                        <PromptTextArea
                                            placeholder="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
                                            value={promptGeracao}
                                            onChange={(event) =>
                                                setPromptGeracao(
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </PromptCard>
                                </StepContent>
                            </Step>
                        </Stepper>
                    </SortableContext>
                </DndContext>
            </MinutasWrapper>

            <Footer>
                <Button
                    variant="text"
                    size="small"
                    sx={{
                        color: '#838383',
                        minHeight: '32px',
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleProcessar}
                    disabled={isProcessing}
                    sx={{
                        minHeight: '32px',
                        backgroundColor: '#3ACDB4',
                        '&:hover': {
                            backgroundColor: '#3ACDB4',
                        },
                    }}
                >
                    {isProcessing ? 'Processando...' : 'Processar documentos'}
                </Button>
            </Footer>
        </Section>
    )
}
